import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { FAQAccordion, faqJsonLd } from "@/components/site/FAQAccordion";
import { PeruMap } from "@/components/site/PeruMap";
import coverImg from "@/assets/portfolio-4.jpg";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { DISTRITOS } from "@/data/geo";

const DEFAULT_TITLE = "Cobertura BTL Lima Metropolitana y Callao | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Consulta nuestra cobertura BTL por distrito en Lima Metropolitana y Callao. Coordinamos puntos, horarios, accesos y logística según tu campaña.";
import { SITE_URL } from "@/lib/site";

// Zonas mencionadas como cobertura pero sin página propia (a diferencia de
// DISTRITOS, que sí tiene /activaciones-btl/$distrito e
// /impulsadoras-promotoras/$distrito reales) — se listan como texto plano,
// nunca como link, para no crear enlaces rotos.
const OTRAS_ZONAS = [
  "Magdalena",
  "Pueblo Libre",
  "Independencia",
  "San Martín de Porres",
  "Comas",
  "Santa Anita",
  "Chorrillos",
  "Villa El Salvador",
  "Bellavista",
  "La Perla",
];

const faqs = [
  {
    q: "¿En qué distritos de Lima operan?",
    a: "Operamos en toda Lima Metropolitana y Callao. Los distritos listados son referenciales; podemos cubrir cualquier zona dentro del área metropolitana.",
  },
  {
    q: "¿Atienden centros comerciales y supermercados?",
    a: "Sí. Ejecutamos activaciones, sampling, impulso y trade marketing en cadenas, centros comerciales y comercio de proximidad.",
  },
  {
    q: "¿Pueden ejecutar fuera de Lima?",
    a: "Sí. Coordinamos producción y logística para campañas en provincias según el alcance del proyecto.",
  },
];

export const Route = createFileRoute("/cobertura/lima")({
  component: CoberturaLima,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/cobertura/lima",
          image: override?.ogImage ?? coverImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/cobertura/lima"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Cobertura Lima",
                item: `${SITE_URL}/cobertura/lima`,
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${SITE_URL}/cobertura/lima`,
            url: `${SITE_URL}/cobertura/lima`,
            name: "Cobertura BTL: Lima Metropolitana y Callao",
            inLanguage: "es-PE",
            about: {
              "@type": "LocalBusiness",
              "@id": `${SITE_URL}/#organization`,
              areaServed: [
                { "@type": "City", name: "Lima" },
                { "@type": "City", name: "Callao" },
              ],
            },
          }),
        },
      ],
    };
  },
});

function CoberturaLima() {
  return (
    <PageShell>
      <PageHero
        eyebrow="· Cobertura Lima"
        h1="Cobertura BTL en Lima Metropolitana y Callao"
        intro="Revisa las zonas donde podemos ejecutar tu campaña en Lima Metropolitana y Callao. Coordinamos puntos, horarios, acceso a los espacios y logística según el formato y las fechas. Para otras ciudades, confirmamos el alcance de cada proyecto."
      >
        <Link
          to="/cotizar"
          className="rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-pop ring-4 ring-primary/15"
        >
          Solicitar propuesta →
        </Link>
      </PageHero>

      <p className="mx-auto mb-12 max-w-2xl px-5 text-center text-sm text-secondary/60 md:px-8">
        ¿Quieres entender qué servicios contratar antes de definir la zona? Consulta nuestra guía de{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "todo-lo-que-hace-una-agencia-btl-en-lima-por-tu-marca" }}
          className="font-semibold text-primary hover:underline"
        >
          qué hace una agencia BTL en Lima: servicios, proceso y costos
        </Link>
        .
      </p>

      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div className="flex items-center justify-center aspect-[5/4] bg-gradient-to-br from-muted/60 via-background to-muted/40 p-4">
              <PeruMap className="max-w-[360px]" title="Mapa del Perú con base en Lima" />
            </div>
            <div>
              <h2 className="font-display text-3xl font-bold text-secondary md:text-4xl">
                Distritos donde operamos
              </h2>
              <p className="mt-4 text-lg text-secondary/75">
                Cobertura referencial. Podemos ejecutar en cualquier distrito de Lima Metropolitana
                y Callao según las necesidades del proyecto.
              </p>
              <p className="mt-4 text-base leading-relaxed text-secondary/75">
                Para confirmar la operación, indícanos los distritos, los puntos o direcciones, las
                fechas y los horarios previstos. Revisamos contigo los accesos, los traslados entre
                puntos y las autorizaciones que corresponda coordinar antes de ejecutar.
              </p>
              <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/10 p-5 backdrop-blur-md">
                <h3 className="font-display text-lg font-bold text-secondary">Provincias</h3>
                <p className="mt-2 text-secondary/75">
                  Para campañas fuera de Lima coordinamos producción, personal y logística desde
                  nuestra base. Cuéntanos la ciudad y el alcance para enviarte una propuesta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <h2 className="font-display text-3xl font-bold text-secondary md:text-4xl">
            Cobertura por distrito
          </h2>
          <p className="mt-3 max-w-2xl text-secondary/75">
            Información y propuesta adaptadas a cada zona.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DISTRITOS.map((d) => (
              <div key={d.slug} className="rounded-2xl border border-border bg-background p-4">
                <p className="font-display font-bold text-secondary">{d.name}</p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>
                    <Link
                      to="/activaciones-btl/$distrito"
                      params={{ distrito: d.slug }}
                      className="text-primary hover:underline"
                    >
                      Activaciones BTL en {d.name}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/impulsadoras-promotoras/$distrito"
                      params={{ distrito: d.slug }}
                      className="text-primary hover:underline"
                    >
                      Impulsadoras y promotoras en {d.name}
                    </Link>
                  </li>
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-secondary/60">
            También operamos en {OTRAS_ZONAS.join(", ")} y el resto de Lima Metropolitana y Callao.
          </p>
        </div>
      </section>

      <FAQAccordion items={faqs} title="Preguntas frecuentes sobre cobertura" />
    </PageShell>
  );
}
